<script lang="ts">
	import { page } from '$app/state';
	import { getFontUrls, getFontFamilyName } from '#lib/helper/font.js';
	import type { Widget, WidgetContent } from '#lib/types/generated/index.js';
	import VideoImage from '#lib/html/VideoImage.svelte';
	import WidgetHeader from '#lib/widget/components/WidgetHeader.svelte';
	import { updateWidgetTheme } from '#lib/widget/theme.js';
	import { widgetConfig, widgetContent } from '#lib/widget/core.js';
	import { onMount, untrack } from 'svelte';
	import { fade } from 'svelte/transition';

	interface Props {
		config: Widget;
		content?: WidgetContent | undefined;
		children?: import('svelte').Snippet;
		preview?: boolean;
		hideHeader?: boolean;
		hideBackground?: boolean;
	}

	let {
		config,
		content = undefined,
		children,
		preview = false,
		hideHeader = false,
		hideBackground = false
	}: Props = $props();

	$effect(() => {
		if (content && preview) {
			$widgetContent = content;
		}
	});

	$effect(() => {
		if (preview) {
			$widgetConfig = config;
		}
	});

	untrack(() => {
		$widgetConfig = config;
	});

	$effect(() => {
		updateWidgetTheme($widgetConfig.theme);
	});
	let fontUrls = $derived(getFontUrls($widgetConfig.theme.fontFamily ?? ''));
	let fontStyle = $derived(
		$widgetConfig.theme.fontFamily && $widgetConfig.theme.fontFamily !== 'default'
			? `font-family: '${getFontFamilyName($widgetConfig.theme.fontFamily)}', sans-serif;`
			: ''
	);
	let hasBg = $derived(
		($widgetConfig.theme.backgroundImage || $widgetConfig.theme.pageBackgroundColor) &&
			!hideBackground
	);
	let hasBgImage = $derived(hasBg && !!$widgetConfig.theme.backgroundImage);

	let viewport: HTMLDivElement | undefined = $state();
	let contentElement: HTMLDivElement | undefined = $state();
	let previousPath: string | null = null;
	let lastSentHeight = 0;

	function sendHeight(routeChanged = false) {
		if (typeof window === 'undefined' || window.parent === window || !contentElement) return;

		const contentRectHeight = contentElement.getBoundingClientRect().height;
		const paddingExtra = hasBg ? 48 : 16;
		const totalHeight = Math.ceil(contentRectHeight + paddingExtra);

		if (totalHeight > 0 && (routeChanged || Math.abs(totalHeight - lastSentHeight) >= 2)) {
			lastSentHeight = totalHeight;
			window.parent.postMessage(
				{
					source: 'reservatoo-widget',
					event: 'resize',
					height: totalHeight,
					routeChanged
				},
				'*'
			);
		}
	}

	onMount(() => {
		if (typeof window === 'undefined' || window.parent === window) return;

		sendHeight();

		let resizeObserver: ResizeObserver | null = null;
		if (typeof ResizeObserver !== 'undefined' && contentElement) {
			resizeObserver = new ResizeObserver(() => {
				sendHeight();
			});
			resizeObserver.observe(contentElement);
		}

		function handleMessage(event: MessageEvent) {
			if (
				event.data &&
				event.data.source === 'reservatoo-parent' &&
				event.data.event === 'request-resize'
			) {
				sendHeight();
			}
		}
		window.addEventListener('message', handleMessage);

		return () => {
			resizeObserver?.disconnect();
			window.removeEventListener('message', handleMessage);
		};
	});

	$effect(() => {
		const currentPath = page.url.pathname;
		const isNav = previousPath !== null && currentPath !== previousPath;
		if (isNav) {
			if (viewport) viewport.scrollTop = 0;
			lastSentHeight = 0;
		}
		previousPath = currentPath;
		if (typeof window !== 'undefined' && window.parent !== window) {
			requestAnimationFrame(() => sendHeight(isNav));
		}
	});
</script>

<svelte:head>
	<link rel="preconnect" href="https://fonts.googleapis.com" />
	<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="" />
	{#if fontUrls}
		{#each fontUrls as fontUrl}
			<link href={fontUrl} rel="stylesheet" />
		{/each}
	{/if}
</svelte:head>

<!-- Background -->
{#if $widgetContent}
	<div
		bind:this={viewport}
		class="flex justify-center md:items-center transition-[padding,background-color] duration-300 ease-in-out {hasBg
			? hasBgImage
				? 'md:p-8 bg-transparent'
				: 'md:p-8 bg-widget-page-background dark:bg-widget-page-background-dark'
			: 'md:p-0 bg-widget-background dark:bg-widget-background-dark'} flex-1 w-full h-full relative overflow-x-hidden text-widget-background-on dark:text-widget-background-dark-on"
		style={fontStyle}
		id="reservatoo-viewport"
	>
		<!-- Background Media -->
		{#if hasBgImage}
			<div
				transition:fade={{ duration: 300 }}
				class="hidden md:block absolute inset-0 w-full h-full object-cover -z-10 pointer-events-none"
			>
				<VideoImage
					src={$widgetConfig.theme.backgroundImage}
					className="w-full h-full object-cover"
				/>
				<!-- Overlay for legibility -->
				<div
					class="absolute inset-0 bg-black/10 dark:bg-black/30 pointer-events-none"
				></div>
			</div>
		{/if}

		<!-- Widget Frame -->
		<div
			class="flex flex-col flex-1 w-full h-full gap-2 sm:gap-5 scrollbar-thin transition-[max-width,border-radius,box-shadow,background-color,backdrop-filter] duration-300 ease-in-out max-w-full
			{hasBgImage
				? 'bg-widget-background dark:bg-widget-background-dark md:bg-widget-background/85 md:dark:bg-widget-background-dark/85 md:backdrop-blur-xl md:border md:border-black/5 md:dark:border-white/10'
				: 'bg-widget-background dark:bg-widget-background-dark'}
			{hasBg
				? 'md:max-w-screen-md lg:max-w-4xl xl:max-w-5xl md:max-h-full overflow-y-auto md:rounded-3xl md:shadow-2xl py-0 px-0 md:pb-6 md:pt-0 md:px-0 md:gap-5'
				: 'md:max-w-5xl'}"
		>
			<div
				bind:this={contentElement}
				class="flex flex-col text-center w-full max-w-full lg:justify-center"
			>
				{#if !hideHeader}
					<WidgetHeader {preview} />
				{/if}
				<!-- Main content -->
				<main
					class="text-center w-full max-w-full flex flex-col lg:flex-row-reverse min-h-80 lg:min-h-96 justify-center {hasBg
						? 'md:px-4'
						: ''}"
				>
					<div class="grow max-w-full {hasBg || preview ? '' : 'px-2'}">
						{@render children?.()}
					</div>
				</main>
			</div>
		</div>
	</div>
{/if}
